import type { StateCreator } from 'zustand';
import type { StoreState, World, Campaign, WorldCalendar, Session, DailyAlmanac, WeatherEvent, Moon, PartyTreasure, WeatherForecast, MoonPhase, WorldSlice } from '../types/types';
import { api } from '../services/api';
import { logger } from '../services/logger.client';

export interface WorldActions {
  fetchWorlds: () => Promise<void>;
  addWorld: (world: Omit<World, 'id' | 'createdAt' | 'updatedAt'>) => Promise<void>;
  updateWorld: (world: Partial<World> & { id: number }) => Promise<void>;
  deleteWorld: (id: number) => Promise<void>;

  fetchCampaigns: () => Promise<void>;
  addCampaign: (campaign: Omit<Campaign, 'id' | 'createdAt' | 'updatedAt'>) => Promise<void>;
  updateCampaign: (campaign: Partial<Campaign> & { id: number }) => Promise<void>;
  deleteCampaign: (id: number) => Promise<void>;

  fetchCalendar: (worldId: number) => Promise<void>;
  upsertCalendar: (worldId: number, calendar: Omit<WorldCalendar, 'id' | 'worldId'>) => Promise<void>;
  deleteCalendar: (worldId: number) => Promise<void>;

  fetchGameSessions: (campaignId: number) => Promise<void>;
  addGameSession: (campaignId: number, session: Omit<Session, 'id' | 'campaignId' | 'createdAt'>) => Promise<void>;
  updateGameSession: (campaignId: number, session: Partial<Session> & { id: number }) => Promise<void>;
  deleteGameSession: (campaignId: number, sessionId: number) => Promise<void>;

  fetchAlmanac: () => Promise<void>;
  upsertAlmanacEntry: (entry: Omit<DailyAlmanac, 'id' | 'createdAt' | 'updatedAt'>) => Promise<void>;
  deleteAlmanacEntry: (date: string) => Promise<void>;

  fetchWeatherEvents: () => Promise<void>;
  addWeatherEvent: (event: Omit<WeatherEvent, 'id' | 'createdAt' | 'updatedAt'>) => Promise<void>;
  updateWeatherEvent: (event: Partial<WeatherEvent> & { id: number }) => Promise<void>;
  deleteWeatherEvent: (id: number) => Promise<void>;

  fetchMoons: () => Promise<void>;
  addMoon: (moon: Omit<Moon, 'id'>) => Promise<void>;
  updateMoon: (moon: Partial<Moon> & { id: number }) => Promise<void>;
  deleteMoon: (id: number) => Promise<void>;

  fetchMoonPhases: (moonId: number) => Promise<void>;
  addMoonPhase: (moonId: number, phase: Omit<MoonPhase, 'id' | 'moonId' | 'createdAt' | 'updatedAt'>) => Promise<void>;
  updateMoonPhase: (moonId: number, phase: Partial<MoonPhase> & { id: number }) => Promise<void>;
  deleteMoonPhase: (moonId: number, phaseId: number) => Promise<void>;

  fetchPartyTreasures: (campaignId: number) => Promise<void>;
  addPartyTreasure: (campaignId: number, treasure: Omit<PartyTreasure, 'id' | 'campaignId' | 'createdAt' | 'updatedAt'>) => Promise<void>;
  updatePartyTreasure: (campaignId: number, treasure: Partial<PartyTreasure> & { id: number }) => Promise<void>;
  deletePartyTreasure: (campaignId: number, treasureId: number) => Promise<void>;

  fetchWeatherForecasts: (mapId: number) => Promise<void>;
  addWeatherForecast: (mapId: number, forecast: Omit<WeatherForecast, 'id' | 'mapId' | 'createdAt' | 'updatedAt' | 'weatherCondition'>) => Promise<void>;
  updateWeatherForecast: (mapId: number, forecast: Partial<WeatherForecast> & { id: number }) => Promise<void>;
  deleteWeatherForecast: (mapId: number, forecastId: number) => Promise<void>;
  generateWeatherForecasts: (mapId: number, startDate: string) => Promise<void>;
}

export const createWorldSlice: StateCreator<
  StoreState,
  [['zustand/immer', never]],
  [],
  { world: WorldSlice, get: () => StoreState }
> = (set, get) => ({
  world: {
    worlds: [],
    campaigns: [],
    worldCalendars: {},
    gameSessions: {},
    almanac: [],
    weatherEvents: [],
    partyTreasures: {},
    weatherForecasts: {},
    moonPhases: {},
    moons: [],

    fetchWorlds: async () => {
      try {
        const response = await api('/api/worlds');
        const worlds = await response.json() as World[];
        if (worlds) set(state => { state.session.world.worlds = worlds; });
      } catch (error) {
        logger.error(error, 'Failed to fetch worlds');
      }
    },
    addWorld: async (world: Omit<World, 'id' | 'createdAt' | 'updatedAt'>) => {
      try {
        const response = await api('/api/worlds', { method: 'POST', body: JSON.stringify(world) });
        const newWorld = await response.json() as World;
        if (newWorld) set(state => { state.session.world.worlds.push(newWorld); });
      } catch (error) {
        logger.error(error, 'Failed to add world');
        throw error;
      }
    },
    updateWorld: async (world: Partial<World> & { id: number }) => {
      try {
        const response = await api(`/api/worlds/${world.id}`, { method: 'PUT', body: JSON.stringify(world) });
        const updatedWorld = await response.json() as World;
        if (updatedWorld) {
          set(state => {
            const index = state.session.world.worlds.findIndex((w: World) => w.id === updatedWorld.id);
            if (index !== -1) state.session.world.worlds[index] = updatedWorld;
          });
        }
      } catch (error) {
        logger.error(error, `Failed to update world ${world.id}`);
        throw error;
      }
    },
    deleteWorld: async (id: number) => {
      try {
        await api(`/api/worlds/${id}`, { method: 'DELETE' });
        set(state => { state.session.world.worlds = (state.session.world.worlds ?? []).filter((w: World) => w.id !== id); });
      } catch (error) {
        logger.error(error, `Failed to delete world ${id}`);
        throw error;
      }
    },

    fetchCampaigns: async () => {
      try {
        const response = await api('/api/campaigns');
        const campaigns = await response.json() as Campaign[];
        if (campaigns) set(state => { state.session.world.campaigns = campaigns; });
      } catch (error) {
        logger.error(error, 'Failed to fetch campaigns');
      }
    },
    addCampaign: async (campaign: Omit<Campaign, 'id' | 'createdAt' | 'updatedAt'>) => {
      try {
        const response = await api('/api/campaigns', { method: 'POST', body: JSON.stringify(campaign) });
        const newCampaign = await response.json() as Campaign;
        if (newCampaign) set(state => { state.session.world.campaigns.push(newCampaign); });
      } catch (error) {
        logger.error(error, 'Failed to add campaign');
        throw error;
      }
    },
    updateCampaign: async (campaign: Partial<Campaign> & { id: number }) => {
      try {
        const response = await api(`/api/campaigns/${campaign.id}`, { method: 'PUT', body: JSON.stringify(campaign) });
        const updatedCampaign = await response.json() as Campaign;
        if (updatedCampaign) {
          set(state => {
            const index = state.session.world.campaigns.findIndex((c: Campaign) => c.id === updatedCampaign.id);
            if (index !== -1) state.session.world.campaigns[index] = updatedCampaign;
          });
        }
      } catch (error) {
        logger.error(error, `Failed to update campaign ${campaign.id}`);
        throw error;
      }
    },
    deleteCampaign: async (id: number) => {
      try {
        await api(`/api/campaigns/${id}`, { method: 'DELETE' });
        set(state => { state.session.world.campaigns = (state.session.world.campaigns ?? []).filter((c: Campaign) => c.id !== id); });
      } catch (error) {
        logger.error(error, `Failed to delete campaign ${id}`);
        throw error;
      }
    },

    fetchCalendar: async (worldId: number) => {
      try {
        const response = await api(`/api/worlds/${worldId}/calendar`);
        const calendar = await response.json() as WorldCalendar | null;
        if (calendar !== undefined) {
          set(state => { state.session.world.worldCalendars[worldId] = calendar; });
        }
      } catch (error) {
        logger.error(error, `Failed to fetch calendar for world ${worldId}`);
      }
    },
    upsertCalendar: async (worldId: number, calendar: Omit<WorldCalendar, 'id' | 'worldId'>) => {
      try {
        const response = await api(`/api/worlds/${worldId}/calendar`, { method: 'PUT', body: JSON.stringify(calendar) });
        const newCalendar = await response.json() as WorldCalendar;
        if (newCalendar) {
          set(state => { state.session.world.worldCalendars[worldId] = newCalendar; });
        }
      } catch (error) {
        logger.error(error, `Failed to upsert calendar for world ${worldId}`);
        throw error;
      }
    },
    deleteCalendar: async (worldId: number) => {
      try {
        await api(`/api/worlds/${worldId}/calendar`, { method: 'DELETE' });
        set(state => { state.session.world.worldCalendars[worldId] = null; });
      } catch (error) {
        logger.error(error, `Failed to delete calendar for world ${worldId}`);
        throw error;
      }
    },

    fetchGameSessions: async (campaignId: number) => {
      try {
        const response = await api(`/api/campaigns/${campaignId}/sessions`);
        const sessions = await response.json() as Session[];
        if (sessions) set(state => { state.session.world.gameSessions[campaignId] = sessions; });
      } catch (error) {
        logger.error(error, `Failed to fetch game sessions for campaign ${campaignId}`);
      }
    },
    addGameSession: async (campaignId: number, session: Omit<Session, 'id' | 'campaignId' | 'createdAt'>) => {
      try {
        const response = await api(`/api/campaigns/${campaignId}/sessions`, { method: 'POST', body: JSON.stringify(session) });
        const newSession = await response.json() as Session;
        if (newSession) {
          set(state => {
            state.session.world.gameSessions[campaignId] ??= [];
            state.session.world.gameSessions[campaignId].push(newSession);
          });
        }
      } catch (error) {
        logger.error(error, `Failed to add game session for campaign ${campaignId}`);
        throw error;
      }
    },
    updateGameSession: async (campaignId: number, session: Partial<Session> & { id: number }) => {
      try {
        const response = await api(`/api/campaigns/${campaignId}/sessions/${session.id}`, { method: 'PUT', body: JSON.stringify(session) });
        const updatedSession = await response.json() as Session;
        if (updatedSession) {
          set(state => {
            const sessions = state.session.world.gameSessions[campaignId]!;
            const index = sessions.findIndex((s: Session) => s.id === updatedSession.id);
            if (index !== -1) sessions[index] = updatedSession;
          });
        }
      } catch (error) {
        logger.error(error, `Failed to update game session ${session.id}`);
        throw error;
      }
    },
    deleteGameSession: async (campaignId: number, sessionId: number) => {
      try {
        await api(`/api/campaigns/${campaignId}/sessions/${sessionId}`, { method: 'DELETE' });
        set(state => { state.session.world.gameSessions[campaignId] = (state.session.world.gameSessions[campaignId]! ?? []).filter((s: Session) => s.id !== sessionId); });
      } catch (error) {
        logger.error(error, `Failed to delete game session ${sessionId}`);
        throw error;
      }
    },

    fetchAlmanac: async () => {
      try {
        const response = await api('/api/almanac');
        const almanacEntries = await response.json() as DailyAlmanac[];
        if (almanacEntries) {
          set(state => { state.session.world.almanac = almanacEntries; });
        }
      } catch (error) {
        logger.error(error, 'Failed to fetch almanac');
      }
    },
    upsertAlmanacEntry: async (entry: Omit<DailyAlmanac, 'id' | 'createdAt' | 'updatedAt'>) => {
      try {
        const dateString = new Date(entry.date).toISOString().split('T')[0];
        const response = await api(`/api/almanac/${dateString}`, { method: 'PUT', body: JSON.stringify(entry) });
        const newOrUpdatedEntry = await response.json() as DailyAlmanac;
        if (newOrUpdatedEntry) {
          set(state => {
            const index = state.session.world.almanac.findIndex((e: DailyAlmanac) => new Date(e.date).toISOString().split('T')[0] === dateString);
            if (index !== -1) {
              state.session.world.almanac[index] = newOrUpdatedEntry;
            } else {
              state.session.world.almanac.push(newOrUpdatedEntry);
              state.session.world.almanac.sort((a: DailyAlmanac, b: DailyAlmanac) => new Date(a.date).getTime() - new Date(b.date).getTime());
            }
          });
        }
      } catch (error) {
        logger.error(error, 'Failed to upsert almanac entry');
        throw error;
      }
    },
    deleteAlmanacEntry: async (date: string) => {
      try {
        await api(`/api/almanac/${date}`, { method: 'DELETE' });
        set(state => { state.session.world.almanac = (state.session.world.almanac ?? []).filter((e: DailyAlmanac) => new Date(e.date).toISOString().split('T')[0] !== date); });
      } catch (error) {
        logger.error(error, `Failed to delete almanac entry for ${date}`);
        throw error;
      }
    },

    fetchWeatherEvents: async () => {
      try {
        const response = await api('/api/weather-events');
        const events = await response.json() as WeatherEvent[];
        if (events) {
          set(state => { state.session.world.weatherEvents = events; });
        }
      } catch (error) {
        logger.error(error, 'Failed to fetch weather events');
      }
    },
    addWeatherEvent: async (event: Omit<WeatherEvent, 'id' | 'createdAt' | 'updatedAt'>) => {
      try {
        const response = await api('/api/weather-events', { method: 'POST', body: JSON.stringify(event) });
        const newEvent = await response.json() as WeatherEvent;
        if (newEvent) {
          set(state => { state.session.world.weatherEvents.push(newEvent); });
        }
      } catch (error) {
        logger.error(error, 'Failed to add weather event');
        throw error;
      }
    },
    updateWeatherEvent: async (event: Partial<WeatherEvent> & { id: number }) => {
      try {
        const response = await api(`/api/weather-events/${event.id}`, { method: 'PUT', body: JSON.stringify(event) });
        const updatedEvent = await response.json() as WeatherEvent;
        if (updatedEvent) {
          set(state => {
            const index = state.session.world.weatherEvents.findIndex((e: WeatherEvent) => e.id === updatedEvent.id);
            if (index !== -1) state.session.world.weatherEvents[index] = updatedEvent;
          });
        }
      } catch (error) {
        logger.error(error, `Failed to update weather event ${event.id}`);
        throw error;
      }
    },
    deleteWeatherEvent: async (id: number) => {
      try {
        await api(`/api/weather-events/${id}`, { method: 'DELETE' });
        set(state => { state.session.world.weatherEvents = (state.session.world.weatherEvents ?? []).filter((e: WeatherEvent) => e.id !== id); });
      } catch (error) {
        logger.error(error, `Failed to delete weather event ${id}`);
        throw error;
      }
    },

    fetchPartyTreasures: async (campaignId: number) => {
      try {
        const response = await api(`/api/campaigns/${campaignId}/party-treasure`);
        const treasures = await response.json() as PartyTreasure[];
        if (treasures) {
          set(state => { state.session.world.partyTreasures[campaignId] = treasures; });
        }
      } catch (error) {
        logger.error(error, `Failed to fetch party treasures for campaign ${campaignId}`);
      }
    },
    addPartyTreasure: async (campaignId: number, treasure: Omit<PartyTreasure, 'id' | 'campaignId' | 'createdAt' | 'updatedAt'>) => {
      try {
        const response = await api(`/api/campaigns/${campaignId}/party-treasure`, { method: 'POST', body: JSON.stringify(treasure) });
        const newTreasure = await response.json() as PartyTreasure;
        if (newTreasure) {
          set(state => {
            state.session.world.partyTreasures[campaignId] ??= [];
            state.session.world.partyTreasures[campaignId].push(newTreasure);
          });
        }
      } catch (error) {
        logger.error(error, `Failed to add party treasure for campaign ${campaignId}`);
        throw error;
      }
    },
    updatePartyTreasure: async (campaignId: number, treasure: Partial<PartyTreasure> & { id: number }) => {
      try {
        const response = await api(`/api/campaigns/${campaignId}/party-treasure/${treasure.id}`, { method: 'PUT', body: JSON.stringify(treasure) });
        const updatedTreasure = await response.json() as PartyTreasure;
        if (updatedTreasure) {
          set(state => {
            const index = state.session.world.partyTreasures[campaignId]!.findIndex((t: PartyTreasure) => t.id === updatedTreasure.id);
            if (index !== -1) state.session.world.partyTreasures[campaignId]![index] = updatedTreasure;
          });
        }
      } catch (error) {
        logger.error(error, `Failed to update party treasure ${treasure.id}`);
        throw error;
      }
    },
    deletePartyTreasure: async (campaignId: number, treasureId: number) => {
      try {
        await api(`/api/campaigns/${campaignId}/party-treasure/${treasureId}`, { method: 'DELETE' });
        set(state => { state.session.world.partyTreasures[campaignId] = (state.session.world.partyTreasures[campaignId]! ?? []).filter((t: PartyTreasure) => t.id !== treasureId); });
      } catch (error) {
        logger.error(error, `Failed to delete party treasure ${treasureId}`);
        throw error;
      }
    },

    fetchMoons: async (): Promise<void> => {
      try {
        const response = await api('/api/moons');
        const moons = await response.json() as Moon[];
        if (moons) {
          set(state => { state.session.world.moons = moons; });
        }
      } catch (error) {
        logger.error(error, 'Failed to fetch moons');
      }
    },
    addMoon: async (moon: Omit<Moon, 'id'>) => {
      try {
        const response = await api('/api/moons', { method: 'POST', body: JSON.stringify(moon) });
        const newMoon = await response.json() as Moon;
        if (newMoon) {
          set(state => { state.session.world.moons.push(newMoon); });
        }
      } catch (error) {
        logger.error(error, 'Failed to add moon');
        throw error;
      }
    },
    updateMoon: async (moon: Partial<Moon> & { id: number }) => {
      try {
        const response = await api(`/api/moons/${moon.id}`, { method: 'PUT', body: JSON.stringify(moon) });
        const updatedMoon = await response.json() as Moon;
        if (updatedMoon) {
          set(state => {
            const index = state.session.world.moons.findIndex((m: Moon) => m.id === updatedMoon.id);
            if (index !== -1) state.session.world.moons[index] = updatedMoon;
          });
        }
      } catch (error) {
        logger.error(error, `Failed to update moon ${moon.id}`);
        throw error;
      }
    },
    deleteMoon: async (id: number) => {
      try {
        await api(`/api/moons/${id}`, { method: 'DELETE' });
        set(state => { state.session.world.moons = (state.session.world.moons ?? []).filter((m: Moon) => m.id !== id); });
      } catch (error) {
        logger.error(error, `Failed to delete moon ${id}`);
        throw error;
      }
    },

    fetchMoonPhases: async (moonId: number) => {
      try {
        const response = await api(`/api/moons/${moonId}/phases`);
        const phases = await response.json() as MoonPhase[];
        if (phases) {
          set(state => { state.session.world.moonPhases[moonId] = phases; });
        }
      } catch (error) {
        logger.error(error, `Failed to fetch moon phases for moon ${moonId}`);
      }
    },
    addMoonPhase: async (moonId: number, phase: Omit<MoonPhase, 'id' | 'moonId' | 'createdAt' | 'updatedAt'>) => {
      try {
        const response = await api(`/api/moons/${moonId}/phases`, { method: 'POST', body: JSON.stringify(phase) });
        const newPhase = await response.json() as MoonPhase;
        if (newPhase) {
          set(state => {
            state.session.world.moonPhases[moonId] ??= [];
            state.session.world.moonPhases[moonId].push(newPhase);
          });
        }
      } catch (error) {
        logger.error(error, `Failed to add moon phase for moon ${moonId}`);
        throw error;
      }
    },
    updateMoonPhase: async (moonId: number, phase: Partial<MoonPhase> & { id: number }) => {
      try {
        const response = await api(`/api/moons/${moonId}/phases/${phase.id}`, { method: 'PUT', body: JSON.stringify(phase) });
        const updatedPhase = await response.json() as MoonPhase;
        if (updatedPhase) {
          set(state => {
            const index = state.session.world.moonPhases[moonId]!.findIndex((p: MoonPhase) => p.id === updatedPhase.id);
            if (index !== -1) state.session.world.moonPhases[moonId]![index] = updatedPhase;
          });
        }
      } catch (error) {
        logger.error(error, `Failed to update moon phase ${phase.id}`);
        throw error;
      }
    },
    deleteMoonPhase: async (moonId: number, phaseId: number) => {
      try {
        await api(`/api/moons/${moonId}/phases/${phaseId}`, { method: 'DELETE' });
        set(state => { state.session.world.moonPhases[moonId] = (state.session.world.moonPhases[moonId]! ?? []).filter((p: MoonPhase) => p.id !== phaseId); });
      } catch (error) {
        logger.error(error, `Failed to delete moon phase ${phaseId}`);
        throw error;
      }
    },

    fetchWeatherForecasts: async (mapId: number) => {
      try {
        const response = await api(`/api/maps/${mapId}/forecasts`);
        const forecasts = await response.json() as WeatherForecast[];
        if (forecasts) {
          set(state => { state.session.world.weatherForecasts[mapId] = forecasts; });
        }
      } catch (error) {
        logger.error(error, `Failed to fetch weather forecasts for map ${mapId}`);
      }
    },
    addWeatherForecast: async (mapId: number, forecast: Omit<WeatherForecast, 'id' | 'mapId' | 'createdAt' | 'updatedAt' | 'weatherCondition'>) => {
      try {
        const response = await api(`/api/maps/${mapId}/forecasts`, { method: 'POST', body: JSON.stringify(forecast) });
        const newForecast = await response.json() as WeatherForecast;
        if (newForecast) {
          set(state => {
            state.session.world.weatherForecasts[mapId] ??= [];
            state.session.world.weatherForecasts[mapId].push(newForecast);
          });
        }
      } catch (error) {
        logger.error(error, `Failed to add weather forecast for map ${mapId}`);
        throw error;
      }
    },
    updateWeatherForecast: async (mapId: number, forecast: Partial<WeatherForecast> & { id: number }) => {
      try {
        const response = await api(`/api/maps/${mapId}/forecasts/${forecast.id}`, { method: 'PUT', body: JSON.stringify(forecast) });
        const updatedForecast = await response.json() as WeatherForecast;
        if (updatedForecast) {
          set(state => {
            const index = state.session.world.weatherForecasts[mapId]!.findIndex((f: WeatherForecast) => f.id === forecast.id);
            if (index !== -1) state.session.world.weatherForecasts[mapId]![index] = updatedForecast;
          });
        }
      } catch (error) {
        logger.error(error, `Failed to update weather forecast ${forecast.id}`);
        throw error;
      }
    },
    deleteWeatherForecast: async (mapId: number, forecastId: number) => {
      try {
        await api(`/api/maps/${mapId}/forecasts/${forecastId}`, { method: 'DELETE' });
        set(state => { state.session.world.weatherForecasts[mapId] = (state.session.world.weatherForecasts[mapId]! ?? []).filter((f: WeatherForecast) => f.id !== forecastId); });
      } catch (error) {
        logger.error(error, `Failed to delete weather forecast ${forecastId}`);
        throw error;
      }
    },
    generateWeatherForecasts: async (mapId: number, startDate: string) => {
      try {
        await api(`/api/maps/${mapId}/forecasts/generate`, { method: 'POST', body: JSON.stringify({ startDate }) });
        // After generating, refetch the forecasts to update the UI
        get().actions.fetchWeatherForecasts(mapId);
      } catch (error) {
        logger.error(error, `Failed to generate weather forecasts for map ${mapId}`);
        throw error;
      }
    },
  },
  get,
});